import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { VaultState } from '../state';
import { VaultDocument } from '@syfo/types/vault.types';
import type { VaultGetResponse } from '@syfo/types/api.client.types';
import { apiFetch } from '@/lib/api-fetch';
import cryptographerService from '@/services/cryptographer-service';
import { decodeBase64 } from '@/utils/base64';
import { getVaultMetadata, getVaultBlob } from '@/utils/vault-storage';

/**
 * Conflict resolution strategy
 */
export enum ConflictResolutionStrategy {
  /** Keep local changes, discard server changes */
  UseLocal = 'use-local',
  /** Discard local changes, use server version */
  UseServer = 'use-server',
  /** Attempt to merge changes intelligently */
  Merge = 'merge',
  /** Manual resolution required - present both versions to user */
  Manual = 'manual',
}

/**
 * Result of a conflict resolution attempt
 */
export interface ConflictResolution {
  strategy: ConflictResolutionStrategy;
  resolved: boolean;
  localVault: VaultState;
  serverVault: VaultState;
  mergedVault?: VaultState;
  conflicts?: ConflictDetail[];
}

/**
 * Details about a specific conflict
 */
export interface ConflictDetail {
  entity: string; // e.g., 'vehicles', 'people', 'liabilities'
  entityId: string;
  field?: string;
  localValue: unknown;
  serverValue: unknown;
  canAutoResolve: boolean;
  suggestedResolution?: 'local' | 'server' | 'merge';
}

/**
 * Fetch the current server version of the vault
 */
const fetchServerVault = async (
  vaultId: string,
  syncId: string
): Promise<{ vault: VaultState; lastModified: string }> => {
  console.log(`[Conflict Resolution] Fetching server version of vault ${vaultId} (syncId: ${syncId})`);
  
  // Fetch from server API using syncId
  const response = await apiFetch(`/api/vaults/${syncId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    console.error(`[Conflict Resolution] Failed to fetch vault ${syncId}: ${response.status}`);
    throw new Error(`Failed to fetch server vault: ${response.status} ${response.statusText}`);
  }

  const data: VaultGetResponse = await response.json();
  const lastModified = response.headers.get('Last-Modified') || '';
  
  // Decrypt the server vault using local vaultId (cryptographer is keyed by local ID)
  const crypto = cryptographerService.getCryptographer(vaultId);
  if (!crypto) {
    throw new Error('No cryptographer available for this vault');
  }

  const docRaw = await crypto.decryptData(decodeBase64(data.data));
  const rawDoc = JSON.parse(docRaw);
  
  // Deserialize entire document using comprehensive VaultDocument schema
  const doc = VaultDocument.deserialize(rawDoc);

  // Get vault name from local metadata (no longer in API response)
  const localMeta = getVaultMetadata(vaultId);
  const name = localMeta?.name || 'Unnamed Vault';

  return {
    vault: {
      info: { id: vaultId, name },
      syncState: { 
        lastSyncDate: new Date().toISOString(), 
        lastChangeDate: new Date().toISOString(), 
        serverLastModified: lastModified,
        syncId
      },
      data: doc,
    },
    lastModified,
  };
};

/**
 * Detect conflicts between local and server versions
 */
const detectConflicts = (
  localVault: VaultState,
  serverVault: VaultState
): ConflictDetail[] => {
  const conflicts: ConflictDetail[] = [];

  // Compare each entity collection
  const entityTypes = [
    'people',
    'vehicles',
    'properties',
    'liabilities',
    'items',
    'protection',
    'savings',
    'incomes',
    'expenditures',
    'documents',
  ] as const;

  entityTypes.forEach((entityType) => {
// eslint-disable-next-line @typescript-eslint/no-explicit-any, security/detect-object-injection
      const localEntities = (localVault.data[entityType] as any[]) || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, security/detect-object-injection
    const serverEntities = (serverVault.data[entityType] as any[]) || [];

    // Create maps for efficient lookup
    const localMap = new Map(localEntities.map((e) => [e.id, e]));
    const serverMap = new Map(serverEntities.map((e) => [e.id, e]));

    // Check for conflicts
    const allIds = new Set([...localMap.keys(), ...serverMap.keys()]);

    allIds.forEach((id) => {
      const localEntity = localMap.get(id);
      const serverEntity = serverMap.get(id);

      // Entity exists in local but not server (deleted on server)
      if (localEntity && !serverEntity) {
        conflicts.push({
          entity: entityType,
          entityId: id,
          localValue: localEntity,
          serverValue: null,
          canAutoResolve: false,
          suggestedResolution: 'local', // Keep local addition
        });
      }
      // Entity exists on server but not local (deleted locally)
      else if (!localEntity && serverEntity) {
        conflicts.push({
          entity: entityType,
          entityId: id,
          localValue: null,
          serverValue: serverEntity,
          canAutoResolve: false,
          suggestedResolution: 'server', // Keep server version
        });
      }
      // Entity exists in both - check for modifications
      else if (localEntity && serverEntity) {
        const localJson = JSON.stringify(localEntity);
        const serverJson = JSON.stringify(serverEntity);

        if (localJson !== serverJson) {
          // Entities differ - field-level comparison would go here
          conflicts.push({
            entity: entityType,
            entityId: id,
            localValue: localEntity,
            serverValue: serverEntity,
            canAutoResolve: canAutoMerge(localEntity, serverEntity),
            suggestedResolution: 'merge',
          });
        }
      }
    });
  });

  return conflicts;
};

/**
 * Determine if two versions of an entity can be automatically merged
 */
const canAutoMerge = (_localEntity: unknown, _serverEntity: unknown): boolean => {
  // For now, use a simple heuristic
  // More sophisticated logic could check timestamps, field-level changes, etc.
  // Future enhancement: compare field-by-field to detect non-overlapping changes
  return false; // Conservative: require manual resolution
};

/**
 * Merge two vault versions using the specified strategy
 */
const mergeVaults = (
  localVault: VaultState,
  serverVault: VaultState,
  strategy: ConflictResolutionStrategy,
  conflicts: ConflictDetail[]
): VaultState => {
  switch (strategy) {
    case ConflictResolutionStrategy.UseLocal:
      // Keep all local changes
      return {
        ...localVault,
        syncState: {
          ...localVault.syncState,
          serverLastModified: serverVault.syncState.serverLastModified, // Use server's Last-Modified
        },
      };

    case ConflictResolutionStrategy.UseServer:
      // Use server version entirely
      return serverVault;

    case ConflictResolutionStrategy.Merge: {
      // Attempt intelligent merge
      const merged = { ...localVault };

      conflicts.forEach((conflict) => {
        if (conflict.canAutoResolve) {
          // Auto-resolve based on suggestion
          if (conflict.suggestedResolution === 'server') {
            // Apply server value
            const entityKey = conflict.entity as keyof VaultDocument;
            if (entityKey in merged.data) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any, security/detect-object-injection
              const entities = (merged.data[entityKey] as any[]) || [];
              const index = entities.findIndex((e) => e.id === conflict.entityId);
              if (index >= 0) {
                entities.splice(index, 1, conflict.serverValue);
              } else if (conflict.serverValue) {
                entities.push(conflict.serverValue);
              }
            }
          }
          // 'local' means do nothing (keep local)
        }
      });

      merged.syncState.serverLastModified = serverVault.syncState.serverLastModified;
      return merged;
    }

    case ConflictResolutionStrategy.Manual:
      // Return local vault as-is; UI will handle manual resolution
      return localVault;

    default:
      return localVault;
  }
};

/**
 * Thunk to resolve a sync conflict
 */
export const resolveConflictThunk = createAsyncThunk<
  ConflictResolution,
  { vaultId: string; strategy?: ConflictResolutionStrategy },
  { state: RootState }
>(
  'sync/resolveConflict',
  async ({ vaultId, strategy = ConflictResolutionStrategy.Manual }, { getState }) => {
    console.log(`[Conflict Resolution] Resolving conflict for vault ${vaultId}`);

    // Check if we have a cryptographer (vault is unlocked)
    const crypto = cryptographerService.getCryptographer(vaultId);
    const isVaultUnlocked = !!crypto;

    if (!isVaultUnlocked) {
      console.log(`[Conflict Resolution] Vault ${vaultId} is locked - cannot decrypt for detailed comparison`);
      
      // Return a minimal conflict resolution that indicates the vault is locked
      // The UI will show a simpler dialog asking user to choose without details
      return {
        strategy: ConflictResolutionStrategy.Manual,
        resolved: false,
        localVault: {
          info: { id: vaultId, name: 'Locked Vault' },
          syncState: { lastSyncDate: '', lastChangeDate: '' },
          data: {} as VaultDocument,
        },
        serverVault: {
          info: { id: vaultId, name: 'Server Version' },
          syncState: { lastSyncDate: '', lastChangeDate: '' },
          data: {} as VaultDocument,
        },
        conflicts: [{
          entity: 'vault',
          entityId: vaultId,
          field: 'locked',
          localValue: 'Vault is locked',
          serverValue: 'Cannot compare - vault is locked',
          canAutoResolve: false,
          suggestedResolution: undefined,
        }],
      };
    }

    // Get local vault from state or load from localStorage
    const state = getState();
    let localVault = state.vault.currentVault;

    // Get vault metadata to retrieve syncId
    const metadata = getVaultMetadata(vaultId);
    if (!metadata) {
      throw new Error(`Vault ${vaultId} not found in localStorage`);
    }
    
    const syncId = metadata.syncId;
    if (!syncId) {
      throw new Error(`Vault ${vaultId} has no syncId - cannot resolve conflict with server`);
    }

    if (!localVault || localVault.info.id !== vaultId) {
      console.log(`[Conflict Resolution] Vault ${vaultId} not in current state, loading from localStorage...`);
      
      const blob = getVaultBlob(vaultId);
      if (!blob) {
        throw new Error(`Vault ${vaultId} data not found in localStorage`);
      }

      // Decrypt vault data
      const docRaw = await crypto.decryptData(decodeBase64(blob));
      const rawDoc = JSON.parse(docRaw);
      
      // Deserialize entire document using comprehensive VaultDocument schema
      const doc = VaultDocument.deserialize(rawDoc);

      localVault = {
        info: { id: vaultId, name: metadata.name },
        syncState: { 
          lastSyncDate: metadata.lastSyncDate || new Date().toISOString(), 
          lastChangeDate: metadata.modifiedAt || metadata.lastSyncDate || new Date().toISOString(), 
          serverLastModified: metadata.serverLastModified,
          syncId
        },
        data: doc,
      };
      
      console.log(`[Conflict Resolution] Loaded vault ${vaultId} from localStorage`);
    }

    // Fetch server version using syncId
    const { vault: serverVault } = await fetchServerVault(vaultId, syncId);

    // Detect conflicts
    const conflicts = detectConflicts(localVault, serverVault);

    console.log(`[Conflict Resolution] Found ${conflicts.length} conflicts`);

    // If no conflicts, nothing to resolve
    if (conflicts.length === 0) {
      return {
        strategy,
        resolved: true,
        localVault,
        serverVault,
        conflicts: [],
      };
    }

    // Attempt resolution based on strategy
    let mergedVault: VaultState | undefined;
    let resolved = false;

    if (strategy !== ConflictResolutionStrategy.Manual) {
      mergedVault = mergeVaults(localVault, serverVault, strategy, conflicts);
      resolved = true;
    }

    return {
      strategy,
      resolved,
      localVault,
      serverVault,
      mergedVault,
      conflicts,
    };
  }
);
